import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Hero = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-brand-secondary pt-20">
      <motion.div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, #3BDFF8 0%, transparent 50%), radial-gradient(circle at 80% 20%, #021351 0%, transparent 50%)',
        }}
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-brand-accent/10"
            style={{
              width: Math.random() * 300 + 100,
              height: Math.random() * 300 + 100,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{ y: [0, -30, 0], opacity: [0.1, 0.3, 0.1] }}
            transition={{ duration: Math.random() * 5 + 5, repeat: Infinity, delay: Math.random() * 3 }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, type: 'spring', stiffness: 80 }}
          >
            <motion.div
              className="inline-flex items-center gap-2 bg-brand-accent/10 border border-brand-accent/30 rounded-full px-4 py-2 mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <SafeIcon icon={FiIcons.FiHeart} className="text-brand-accent text-sm" />
              <span className="text-brand-accent text-sm font-semibold">A husband & wife team, here for you</span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Let's Tell Your{' '}
              <span className="text-brand-accent relative">
                Story
                <motion.span
                  className="absolute -bottom-1 left-0 h-1 bg-brand-accent rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                />
              </span>{' '}
              the Right Way
            </h1>

            <p className="text-gray-300 text-lg md:text-xl mb-8 leading-relaxed">
              We're a small husband and wife team who genuinely care about helping you put your best foot forward — with a resume and LinkedIn profile that actually sounds like you.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                onClick={scrollToContact}
                className="bg-brand-accent text-brand-secondary font-bold px-8 py-4 rounded-full text-lg shadow-lg hover:shadow-brand-accent/40 transition-all"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                Reach Out to Us
              </motion.button>
              <motion.button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="border border-white/30 text-white font-semibold px-8 py-4 rounded-full text-lg hover:bg-white/10 transition-all flex items-center gap-2"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                See What We Offer <SafeIcon icon={FiIcons.FiArrowRight} />
              </motion.button>
            </div>

            <div className="flex items-center gap-8 mt-10 flex-wrap">
              {[
                { value: '100+', label: 'People We\'ve Helped' },
                { value: 'Real', label: 'Human Support' },
                { value: '5★', label: 'Google Rating' },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + i * 0.15 }}
                >
                  <p className="text-2xl font-extrabold text-brand-accent">{stat.value}</p>
                  <p className="text-gray-400 text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.2, type: 'spring', stiffness: 80 }}
            className="relative hidden lg:block"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=700&q=80"
                alt="Career professional"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-secondary/80 to-transparent" />
              <motion.div
                className="absolute bottom-6 left-6 right-6 bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <p className="text-white font-semibold text-sm mb-1">💬 "They made the whole process so easy."</p>
                <p className="text-gray-300 text-xs">— A happy client, after their LinkedIn refresh</p>
              </motion.div>
            </div>
            <motion.div
              className="absolute -top-6 -right-6 bg-brand-accent rounded-2xl p-4 shadow-xl"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              <SafeIcon icon={FiIcons.FiHeart} className="text-brand-secondary text-3xl" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;